import React, { Fragment, useEffect, useState } from "react";
import { graphql } from "gatsby";
import { MDXProvider } from "@mdx-js/react";
import { MDXRenderer } from "gatsby-plugin-mdx";
//import { Link } from "gatsby"
import Layout from "./layout";
import Slide from "./slide";
import { Timeline, TimelineItem } from "./timeline";
import { SQLTable } from "./sqlTable.js";
import LinkPopover from "./linkPopover";
import hljs from "highlight.js";
//import "highlight.js/styles/darcula.css"
//import "highlight.js/styles/atom-one-dark.css";
import CodePlayground from "./codePlayground";
import { css } from "@emotion/react";

//const shortcodes = { Link, Slide } // Provide common components here

const collapseButtonStyle = css`
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 1000px;
  background: grey;
  font-size: 1.1rem;
  cursor: pointer;
  user-select: none;
  text-align: center;
  color: white;
`;
const startSlide = css`
  text-align: center;
`;

function H1({ children }) {
  return (
    <Fragment>
      <h1>{children}</h1>
    </Fragment>
  );
}

function codeTitle(language) {
  switch (language) {
    case "language-html":
      return "HTML";
    case "language-css":
      return "CSS";
    case "language-js":
      return "JavaScript";
    case "language-ts":
      return "TypeScript";
    case "language-java":
      return "Java";
    case "language-json":
      return "JSON";
    case "language-sql":
      return "SQL";
    case "language-cmd":
      return "Kommandozeile";
    default:
      return "Code";
  }
}

function Code(
  codePGOpen,
  setCodePGOpen,
  setEditorHtml,
  setEditorCss,
  setEditorJs,
  callEditorHtml,
  callEditorCss,
  callEditorJs,
  { children, className, ...rest }
) {
  const code = React.useRef(null);
  useEffect(() => {
    hljs.highlightElement(code.current);
  }, []);
  return (
    <Fragment>
      {(className === "language-html" ||
        className === "language-css" ||
        className === "language-js" ||
        className === "language-ts") && (
        <button
          onClick={() => {
            setCodePGOpen(true);
            switch (className) {
              case "language-html":
                //setEditorHtml("")
                //requestAnimationFrame(() => setEditorHtml(children))
                codePGOpen ? callEditorHtml(children) : setEditorHtml(children);
                break;
              case "language-css":
                codePGOpen ? callEditorCss(children) : setEditorCss(children);
                break;
              case "language-js":
              case "language-ts":
                codePGOpen ? callEditorJs(children) : setEditorJs(children);
                break;
            }
          }}
        >
          {String.fromCharCode(0x276e) + String.fromCharCode(0x276f)}
        </button>
      )}
      <span style={{ paddingLeft: "0.5em" }}>{codeTitle(className)}</span>
      <code className={className} ref={code} {...rest}>
        {children}
      </code>
    </Fragment>
  );
}

function MyLink({ href, className, ...rest }) {
  return className === "footnote-ref" ? (
    <Fragment>
      <LinkPopover
        href={href}
        note={hrefs[href]}
        className={className}
        {...rest}
      />
    </Fragment>
  ) : (
    <Fragment>
      <a
        href={href}
        className={className}
        rel="noopener noreferrer nofollow"
        {...rest}
      />
    </Fragment>
  );
}

const hrefs = [];

function extractFootnodes(node) {
  try {
    React.Children.forEach(
      node.props.children[1].props.children,
      e => (hrefs["#" + e.props.id] = e)
    );
  } catch (e) {}
}

function Wrapper(pageContext, frontmatter, { children }) {
  if (pageContext.index === 0) {
    return (
      <div css={startSlide}>
        <h1
          css={css`
            font-size: 3rem;
            margin-top: 25vh;
          `}
        >
          {frontmatter.title}
        </h1>
        {frontmatter.date}
      </div>
    );
  } else {
    const childrenArray = React.Children.toArray(children);
    const headings = childrenArray.reduce(function (a, e, i) {
      if (e.props) {
        if (e.props.mdxType === "h1" || e.props.mdxType === "h2") a.push(i);
        if (e.props.className === "footnotes") extractFootnodes(e);
      }
      return a;
    }, []);
    const result = childrenArray.slice(
      headings[pageContext.index - 1],
      headings[pageContext.index]
    );
    return result;
  }
}

function AnotherComponent() {
  return <p>Just another component.</p>;
}

export default function PageTemplate({ data: { mdx }, pageContext, location }) {
  const [codePGOpen, setCodePGOpen] = useState(false);
  const [editorHtml, setEditorHtml] = useState("");
  const [editorCss, setEditorCss] = useState("");
  const [editorJs, setEditorJs] = useState("");
  const [callEditorHtml, setCallEditorHtml] = useState(null);
  const [callEditorCss, setCallEditorCss] = useState(null);
  const [callEditorJs, setCallEditorJs] = useState(null);

  return (
    <Layout
      navOpen={location && location.state && location.state.navOpen}
      lightMode={location && location.state && location.state.lightMode}
      prevSlug={pageContext.prevSlug}
      nextSlug={pageContext.nextSlug}
      codePlaygroundOpen={codePGOpen}
      setCodePGOpen={setCodePGOpen}
      collapseButtonStyle={collapseButtonStyle}
      codeEditor={
        <CodePlayground
          codePlaygroundOpen={codePGOpen}
          collapseButtonStyle={collapseButtonStyle}
          editorHtml={editorHtml}
          editorCss={editorCss}
          editorJs={editorJs}
          setEditorHtml={setEditorHtml}
          setEditorCss={setEditorCss}
          setEditorJs={setEditorJs}
          setCallEditorHtml={setCallEditorHtml}
          setCallEditorCss={setCallEditorCss}
          setCallEditorJs={setCallEditorJs}
        />
      }
    >
      <div>
        <MDXProvider
          components={{
            h1: H1,
            h2: H1,
            a: MyLink,
            code: Code.bind(
              this,
              codePGOpen,
              setCodePGOpen,
              setEditorHtml,
              setEditorCss,
              setEditorJs,
              callEditorHtml,
              callEditorCss,
              callEditorJs
            ),
            wrapper: Wrapper.bind(this, pageContext, mdx.frontmatter),
            AnotherComponent,
            Slide,
            Timeline,
            TimelineItem,
            SQLTable,
          }}
        >
          <MDXRenderer>{mdx.body}</MDXRenderer>
        </MDXProvider>
      </div>
    </Layout>
  );
}

export const pageQuery = graphql`
  query LectureQuery($id: String) {
    mdx(id: { eq: $id }) {
      id
      body
      frontmatter {
        title
        date(formatString: "DD. MMMM YYYY", locale: "de")
      }
    }
  }
`;

/*<MDXProvider components={shortcodes}>
          <MDXRenderer>{mdx.body}</MDXRenderer>
        </MDXProvider>*/
